import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { openFile } from '../api/documents.ts';
import {
  PHOTO_TOPICS,
  type PhotoTopic,
  retireVenueFile,
  roomsQuery,
  uploadVenueFile,
  VENUE_FILE_KINDS,
  type VenueFile,
  type VenueFileKind,
  venueFilesQuery,
} from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ VEN-04 and VEN-08 at v1.976 (venue slice 2C): a venue's floor and rigging plans and its
// photos. A new plan for a room replaces the live one, which stays listed as an earlier version;
// a photo says what it shows. Opened through a short-lived link, as every file is (ADR-0011).

const KIND_LABEL = Object.fromEntries(VENUE_FILE_KINDS) as Record<VenueFileKind, string>;
const TOPIC_LABEL = Object.fromEntries(PHOTO_TOPICS) as Record<PhotoTopic, string>;
const day = (iso: string) => iso.slice(0, 10);

export function PlansAndPhotos({ venueId, mayManage }: { venueId: string; mayManage: boolean }) {
  const files = useQuery(venueFilesQuery(venueId));
  const [adding, setAdding] = useState(false);
  const all = files.data ?? [];
  const plans = all.filter((f) => f.kind !== 'photo');
  const live = plans.filter((f) => !f.supersededAt);
  const photos = all.filter((f) => f.kind === 'photo');
  return (
    <section aria-labelledby="plans-photos" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="plans-photos" className="section-title">
          Plans and photos
        </h2>
        {mayManage && !adding ? (
          <Button variant="quiet" onClick={() => setAdding(true)}>
            Upload
          </Button>
        ) : null}
      </div>
      {adding ? <UploadForm venueId={venueId} onDone={() => setAdding(false)} /> : null}
      {files.isError ? (
        <p role="alert" className="alert">
          {files.error.message}
        </p>
      ) : null}
      {files.data && all.length === 0 ? (
        <p className="text-sm text-ink-soft">No plans or photos yet.</p>
      ) : null}
      {live.length > 0 ? (
        <ul aria-label="Plans" className="flex flex-col gap-2 text-sm">
          {live.map((p) => (
            <PlanRow
              key={p.id}
              venueId={venueId}
              plan={p}
              earlier={earlierThan(p, plans)}
              mayManage={mayManage}
            />
          ))}
        </ul>
      ) : null}
      {photos.length > 0 ? (
        <ul aria-label="Photos" className="flex flex-col gap-2 text-sm">
          {photos.map((p) => (
            <FileRow key={p.id} venueId={venueId} file={p} mayManage={mayManage}>
              <span className="tag">{p.topic ? TOPIC_LABEL[p.topic] : 'Photo'}</span>
              {p.room ? <span className="text-ink-soft">{p.room.name}</span> : null}
            </FileRow>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** The chain of plans a live one superseded, newest first. */
function earlierThan(plan: VenueFile, plans: readonly VenueFile[]): VenueFile[] {
  const byId = new Map(plans.map((p) => [p.id, p]));
  const chain: VenueFile[] = [];
  let next = plan.supersedesId ? byId.get(plan.supersedesId) : undefined;
  while (next) {
    chain.push(next);
    next = next.supersedesId ? byId.get(next.supersedesId) : undefined;
  }
  return chain;
}

function PlanRow({
  venueId,
  plan,
  earlier,
  mayManage,
}: {
  venueId: string;
  plan: VenueFile;
  earlier: readonly VenueFile[];
  mayManage: boolean;
}) {
  const [showing, setShowing] = useState(false);
  return (
    <FileRow venueId={venueId} file={plan} mayManage={mayManage}>
      <span className="font-semibold">{KIND_LABEL[plan.kind]}</span>
      <span className="text-ink-soft">{plan.room?.name ?? 'Whole venue'}</span>
      {earlier.length > 0 ? (
        <span className="w-full">
          <button
            type="button"
            className="link"
            aria-expanded={showing}
            onClick={() => setShowing((s) => !s)}
          >
            Previous versions ({earlier.length}){' '}
            <span aria-hidden="true">{showing ? '▴' : '▾'}</span>
          </button>
          {showing ? (
            <ul className="mt-1 flex flex-col gap-1 pl-4">
              {earlier.map((e) => (
                <li key={e.id} className="flex flex-wrap items-baseline gap-2 text-ink-soft">
                  Uploaded {day(e.createdAt)}, replaced {day(e.supersededAt ?? '')}
                  <OpenButton fileId={e.file.id} label={`the plan from ${day(e.createdAt)}`} />
                </li>
              ))}
            </ul>
          ) : null}
        </span>
      ) : null}
    </FileRow>
  );
}

export function OpenButton({ fileId, label }: { fileId: string; label: string }) {
  const open = useMutation({ mutationFn: () => openFile(fileId) });
  return (
    <>
      <button type="button" className="link" onClick={() => open.mutate()}>
        Open<span className="sr-only"> {label}</span>
      </button>
      {open.error ? (
        <span role="alert" className="font-semibold text-accent-2-700">
          {open.error.message}
        </span>
      ) : null}
    </>
  );
}

function FileRow({
  venueId,
  file: f,
  mayManage,
  children,
}: {
  venueId: string;
  file: VenueFile;
  mayManage: boolean;
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const retire = useMutation({
    mutationFn: () => retireVenueFile(venueId, f.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: venueFilesQuery(venueId).queryKey }),
  });
  const label = `${KIND_LABEL[f.kind]}${f.room ? ` of ${f.room.name}` : ''}`;
  return (
    <li className="panel flex flex-wrap items-baseline gap-2 p-3">
      {children}
      {f.caption ? <span>{f.caption}</span> : null}
      <span className="text-ink-soft">{day(f.createdAt)}</span>
      <OpenButton fileId={f.file.id} label={label} />
      {mayManage ? (
        confirming ? (
          <button
            type="button"
            className="link"
            disabled={retire.isPending}
            onClick={() => retire.mutate()}
          >
            Yes, retire it<span className="sr-only"> ({label})</span>
          </button>
        ) : (
          <button type="button" className="link" onClick={() => setConfirming(true)}>
            Retire<span className="sr-only"> {label}</span>
          </button>
        )
      ) : null}
      {retire.error ? (
        <span role="alert" className="w-full font-semibold text-accent-2-700">
          {retire.error.message}
        </span>
      ) : null}
    </li>
  );
}

function UploadForm({ venueId, onDone }: { venueId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const rooms = useQuery(roomsQuery(venueId));
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<VenueFileKind>('photo');
  const [roomId, setRoomId] = useState('');
  const [topic, setTopic] = useState<PhotoTopic>('dock');
  const [caption, setCaption] = useState('');
  const [problem, setProblem] = useState<string | undefined>();
  const upload = useMutation({
    mutationFn: (chosen: File) =>
      uploadVenueFile(venueId, chosen, {
        kind,
        roomId: roomId || null,
        topic: kind === 'photo' ? topic : null,
        caption: caption.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: venueFilesQuery(venueId).queryKey });
      onDone();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!file) return setProblem('Choose a file.');
    setProblem(undefined);
    upload.mutate(file);
  }

  return (
    <form
      onSubmit={submit}
      aria-label="Upload a plan or photo"
      className="panel flex flex-col gap-3 p-3"
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="venue-file-kind" className="text-xs text-ink-soft">
            What it is
          </label>
          <select
            id="venue-file-kind"
            className="input"
            value={kind}
            onChange={(e) => setKind(e.currentTarget.value as VenueFileKind)}
          >
            {VENUE_FILE_KINDS.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="venue-file-room" className="text-xs text-ink-soft">
            Room
          </label>
          <select
            id="venue-file-room"
            className="input"
            value={roomId}
            onChange={(e) => setRoomId(e.currentTarget.value)}
          >
            <option value="">Whole venue</option>
            {(rooms.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        {kind === 'photo' ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="venue-file-topic" className="text-xs text-ink-soft">
              It shows
            </label>
            <select
              id="venue-file-topic"
              className="input"
              value={topic}
              onChange={(e) => setTopic(e.currentTarget.value as PhotoTopic)}
            >
              {PHOTO_TOPICS.map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>
      {/* What it is comes first: the file picker accepts that kind's types. */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="venue-file" className="text-xs text-ink-soft">
          File ({kind === 'photo' ? 'PNG/JPG' : 'PDF/PNG/JPG'}, max 20 MB)
        </label>
        <input
          id="venue-file"
          type="file"
          accept={
            kind === 'photo' ? 'image/png,image/jpeg' : 'application/pdf,image/png,image/jpeg'
          }
          onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
        />
      </div>
      {kind !== 'photo' ? (
        <p className="text-xs text-ink-soft">
          A new plan for the same room replaces the current one, which stays listed as earlier.
        </p>
      ) : null}
      <Field
        label="Caption (optional)"
        value={caption}
        onChange={(e) => setCaption(e.currentTarget.value)}
      />
      {problem || upload.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? upload.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={upload.isPending}>
          {upload.isPending ? 'Uploading…' : 'Upload'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * VEN-03 (venue slice 2C): a room's live floor and rigging plans, to open from its card. A SWMS for
 * the venue is listed beside the rigging by the caller.
 */
export function RoomPlans({ venueId, roomId }: { venueId: string; roomId: string }) {
  const files = useQuery(venueFilesQuery(venueId));
  const plans = (files.data ?? []).filter(
    (f) => f.kind !== 'photo' && !f.supersededAt && f.room?.id === roomId,
  );
  if (plans.length === 0) return null;
  return (
    <p className="flex flex-wrap items-baseline gap-2">
      {plans.map((p) => (
        <span key={p.id} className="flex items-baseline gap-1">
          {KIND_LABEL[p.kind]}:{' '}
          <OpenButton fileId={p.file.id} label={`${KIND_LABEL[p.kind]} of ${p.room!.name}`} />
        </span>
      ))}
    </p>
  );
}
